import noop from 'lodash/noop';
import React, {useMemo} from 'react';

import Backdrop from '@material-ui/core/Backdrop';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles, useTheme} from '@material-ui/core/styles';

import {ModalContext} from 'components/modals/modal-context';

import type {DialogProps} from '@material-ui/core/Dialog/Dialog';

const useStyles = makeStyles((theme) => {
    const paperCommon = {
        boxShadow: theme.shadows[1],
        borderRadius: '0.5rem',
        overflow: 'visible',
    };

    return {
        paperSmall: {
            ...paperCommon,
            minWidth: '32rem',
            maxWidth: '47.9rem',
        },
        paperMedium: {
            ...paperCommon,
            minWidth: '48rem',
            maxWidth: '63.9rem',
        },
        paperLarge: {
            ...paperCommon,
            minWidth: '64rem',
            maxWidth: '72rem',
        },
    };
});

export type Props = {
    children: React.ReactNode;
    open: DialogProps['open'];
    onClose?: () => void | undefined;
    onExited?: () => void | undefined;
    size?: 'small' | 'medium' | 'large';
};

const SCModal = (props: Props) => {
    const {open, onClose = noop, onExited = noop, size = 'medium', children} = props;
    const classes = useStyles();
    const theme = useTheme();
    const timeout = useMemo(
        () => ({
            enter: theme.transitions.duration.enteringScreen,
            exit: theme.transitions.duration.leavingScreen,
        }),
        [theme.transitions.duration.enteringScreen, theme.transitions.duration.leavingScreen],
    );
    const modalContextValue = {
        size,
        open,
        onClose,
    };

    let paper: string;
    switch (size) {
        case 'small':
            paper = classes.paperSmall;
            break;
        case 'large':
            paper = classes.paperLarge;
            break;
        case 'medium':
        default:
            paper = classes.paperMedium;
            break;
    }

    return (
        <Dialog
            classes={{paper}}
            open={open}
            onClose={onClose}
            TransitionProps={{
                onExited,
                timeout,
            }}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout,
            }}>
            <ModalContext.Provider value={modalContextValue}>{children}</ModalContext.Provider>
        </Dialog>
    );
};

export default SCModal;
